import {
  getCircleIndices,
  getIsPointOnCircumferenceValues,
  getNumSegmentsForRadius,
  getThetaValues,
  numFloatsPerNodeInstance,
} from '../../network/chart/Utils';
import {
  ColorQuadruplet, ColorTriplet, IUpdatePatternItem,
  PersistentFloat32Array,
  UpdateType,
} from '../../sharedComponents/webglUtils';
import {
  getColorTripletFromColorString,
} from '../../tree/newChart/domIndependentDrawUtils';
import {
  failIfValidOrNonExhaustive,
} from '../../Utils';
import { ChartNode} from './otherTypes';

export const minNodeRadius = 2;
export const maxNodeRadius = 50;
export const circleNumSegments = getNumSegmentsForRadius(maxNodeRadius, 0.2);
export const nodeFillThetaValues = getThetaValues(circleNumSegments);
export const nodeFillIsPointOnCircumferenceValues = getIsPointOnCircumferenceValues(circleNumSegments);
export const nodeFillIndices = getCircleIndices(circleNumSegments);

export interface NodeInternal {
  id: string;
  centerX: number;
  centerY: number;
  radius: number;
  fillColor: ColorQuadruplet;
  strokeColor: ColorQuadruplet;
  label: string;
  contributingInfo:
    { isAmongLargest: false } |
    { isAmongLargest: true, index: number };
}

export const defaultStrokeWidth = 1;
const defaultNodeOpacity = 0.6;
const hoveredNodeOpacity = 1;
const unhoveredNodeOpacity = 0.2;

export const convertToInternalNodes = (input: {
    nodes: ChartNode[],
    hoveredNode: string | undefined
    unhoveredNodeColor: ColorTriplet,
  }) => {

  const {nodes, hoveredNode, unhoveredNodeColor} = input;

  const nextNodes: Map<string, NodeInternal> = new Map();
  const nextKeys: string[] = [];
  const nodesLength = nodes.length;
  for (let i = 0; i < nodesLength; i += 1) {
    const node = nodes[i];
    const id = node.id;
    const centerX = node.x;
    const centerY = node.y;
    const radius = node.radius;
    const label = node.label;
    const contributingInfo = node.contributingInfo;
    let fillOpacity: number;
    if (hoveredNode === undefined) {
      fillOpacity = defaultNodeOpacity;
    } else if (hoveredNode === id) {
      fillOpacity = hoveredNodeOpacity;
    } else {
      fillOpacity = unhoveredNodeOpacity;
    }
    const fillTriplet = (hoveredNode !== undefined && hoveredNode !== id) ?
                    unhoveredNodeColor :
                    getColorTripletFromColorString(node.color);
    const fillColor = [fillTriplet[0], fillTriplet[1], fillTriplet[2], fillOpacity] as ColorQuadruplet;
    const strokeColor = [fillTriplet[0], fillTriplet[1], fillTriplet[2], 1] as ColorQuadruplet;
    const nextNode: NodeInternal = {
      id, centerX, centerY, radius,
      fillColor,
      strokeColor,
      label,
      contributingInfo,
    };
    nextNodes.set(id, nextNode);
    nextKeys.push(id);
  }

  return {nextNodes, nextKeys};
};

export const writeToNodeBuffers = (input: {
    prevNodes: Map<string, NodeInternal>,
    nextNodes: Map<string, NodeInternal>,
    updatePattern: IUpdatePatternItem[],
    data: PersistentFloat32Array,
    isFirstRender: boolean,
}) => {

  const {prevNodes, nextNodes, updatePattern, data, isFirstRender} = input;

  const updatePatternLength = updatePattern.length;
  const buffer = data.buffer;
  data.length = updatePatternLength * numFloatsPerNodeInstance;

  for (let i = 0; i < updatePatternLength; i += 1) {
    const {key, type} = updatePattern[i];
    let initialCenterX: number, finalCenterX: number;
    let initialCenterY: number, finalCenterY: number;
    let initialRadius: number, finalRadius: number;
    let initialHalfStrokeWidth: number, finalHalfStrokeWidth: number;
    let initialFillColor: ColorQuadruplet, finalFillColor: ColorQuadruplet;

    if (type === UpdateType.Enter) {
      const node = nextNodes.get(key)!;
      initialCenterX = finalCenterX = node.centerX;
      initialCenterY = finalCenterY = node.centerY;
      // If this is the first render, set the initial radius to zero in order to
      // make the nodes "grow" into their full sizes:
      if (isFirstRender === true) {
        initialRadius = 1;
        finalRadius = node.radius;
      } else {
        initialRadius = finalRadius = node.radius;
      }
      initialHalfStrokeWidth = finalHalfStrokeWidth = 0;

      initialFillColor = node.fillColor.slice(0) as ColorQuadruplet;
      initialFillColor[3] = 0;
      finalFillColor = node.fillColor.slice(0) as ColorQuadruplet;
    } else if (type === UpdateType.Exit) {
      const node = prevNodes.get(key)!;
      initialCenterX = finalCenterX = node.centerX;
      initialCenterY = finalCenterY = node.centerY;
      initialRadius = finalRadius = node.radius;
      initialHalfStrokeWidth = finalHalfStrokeWidth = 0;

      initialFillColor = node.fillColor.slice(0) as ColorQuadruplet;
      finalFillColor = node.fillColor.slice(0) as ColorQuadruplet;
      finalFillColor[3] = 0;
    } else if (type === UpdateType.Update) {
      const prevNode = prevNodes.get(key)!;
      const nextNode = nextNodes.get(key)!;
      ({
        centerX: initialCenterX, centerY: initialCenterY, radius: initialRadius,
        /* tslint:disable-next-line:trailing-comma */
        fillColor: initialFillColor,
      } = prevNode);
      ({
        centerX: finalCenterX, centerY: finalCenterY, radius: finalRadius,
        /* tslint:disable-next-line:trailing-comma */
        fillColor: finalFillColor,
      } = nextNode);

      initialHalfStrokeWidth = finalHalfStrokeWidth = 0;
    } else {
      failIfValidOrNonExhaustive(type, 'Invalid update type');
      initialCenterX = finalCenterX = 0;
      initialCenterY = finalCenterY = 0;
      initialRadius = finalRadius = 0;
      initialHalfStrokeWidth = finalHalfStrokeWidth = 0;

      initialFillColor = [0, 0, 0, 0];
      finalFillColor = [0, 0, 0, 0];
    }

    const start = i * numFloatsPerNodeInstance;
    buffer[start + 0] = initialCenterX;
    buffer[start + 1] = initialCenterY;

    buffer[start + 2] = finalCenterX;
    buffer[start + 3] = finalCenterY;

    buffer[start + 4] = initialRadius;
    buffer[start + 5] = finalRadius;

    buffer[start + 6] = initialHalfStrokeWidth;
    buffer[start + 7] = finalHalfStrokeWidth;

    buffer[start + 8] = initialFillColor[0];
    buffer[start + 9] = initialFillColor[1];
    buffer[start + 10] = initialFillColor[2];
    buffer[start + 11] = initialFillColor[0];

    buffer[start + 12] = finalFillColor[0];
    buffer[start + 13] = finalFillColor[1];
    buffer[start + 14] = finalFillColor[2];
    buffer[start + 15] = finalFillColor[3];

    buffer[start + 16] = 0;
  }

  return {
    instanceCount: updatePatternLength,
  };
};
